import { useTranslation } from 'react-i18next';
import { Card, CardContent, Box, Divider, useTheme } from '@mui/material';
import { TextV2 } from '@insights-ltd/design-library/components';
import { ProfileResponse } from 'api';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { AllDialectOptions } from 'types/dialects';
import { getPronounIfSupportedByProfile } from 'domain/profile';
import { getProfileTitleText } from 'utils/generalUtils';
import EmailProgressDialog from 'components/EmailProgressDialog';
import DistributionMethodDialog from 'components/DistributionMethodDialog';
import ProfileArchiveDialog from 'components/ProfileArchiveDialog';
import DownloadProgressDialog from 'components/DownloadProgressDialog/DownloadProgressDialog';
import { formatDateFromIsoString } from '@insights-ltd/design-library/utils';
import { getDownloadDialect } from 'domain/dialect';
import { PreferredDialectAndPronoun } from 'pages/Learner/helper';
import ProfileCardHeader from './ProfileCardHeader';
import ProfileCardActions from './ProfileCardActions';
import {
  useProfileArchive,
  useProfileDownload,
  useProfileEmail,
} from './hooks';

type PersonalImpactProfileCardProps = {
  profile: ProfileResponse;
  learnerId: string;
  disabled?: boolean;
  selectedDialect: AllDialectOptions;
  dialectString?: string;
  onProfileDownloadError: () => void;
  organisationId: string;
};

const PersonalImpactProfileCard = ({
  profile,
  learnerId,
  disabled = false,
  selectedDialect,
  dialectString,
  onProfileDownloadError,
  organisationId,
}: PersonalImpactProfileCardProps) => {
  const { t, i18n } = useTranslation();
  const { language: locale } = i18n;
  const localTheme = useTheme();

  const downloadDialect = getDownloadDialect(selectedDialect, profile.dialect);

  const { showDownloadProgress, setShowDownloadProgress } = useProfileDownload({
    learnerId,
    profileId: profile.id,
    downloadDialect,
    onDownloadError: onProfileDownloadError,
  });

  const {
    onEmailProfile,
    handleSendEmail,
    showEmailProgress,
    setShowEmailProgress,
    showDistOptions,
    setShowDistOptions,
  } = useProfileEmail({
    learnerId,
    profileId: profile.id,
    downloadDialect,
    onEmailError: onProfileDownloadError,
  });

  const {
    showArchiveDialog,
    setShowArchiveDialog,
    archiveError,
    archiveReason,
    setArchiveReason,
    archiveReasonOther,
    setArchiveReasonOther,
    handleArchiveProfile,
    handleProfileArchiveClose,
  } = useProfileArchive({
    learnerId,
    profileId: profile.id,
    organisationId,
  });

  const profileTitle = getProfileTitleText(profile.type, null, locale, t);

  const evaluatorDate = profile.evaluationSubmittedOn
    ? formatDateFromIsoString(profile.evaluationSubmittedOn, locale)
    : null;

  const purchaseDate = profile.createdAt
    ? formatDateFromIsoString(profile.createdAt, locale)
    : null;

  return (
    <>
      <Card
        elevation={0}
        sx={(theme) => ({
          display: 'flex',
          height: '100%',
          border: `1px solid ${theme.palette.grey[500]}`,
          backgroundColor: disabled ? theme.palette.grey[200] : 'white',
          borderColor: disabled ? 'transparent' : undefined,
          '& p, & span': {
            color: localTheme.palette.grey[700],
          },
        })}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            justifyContent: 'space-between',
            '& > div:last-of-type': {
              marginTop: 'auto',
            },
          }}
        >
          <Box
            sx={{
              mb: (theme) => theme.spacing(spacingSizeMap.L),
            }}
          >
            <ProfileCardHeader
              profileTitle={profileTitle}
              actionText={t('ui.event-management.learner.profiles.archive')}
              actionColor={localTheme.palette.error.main}
              onActionClick={() => setShowArchiveDialog(true)}
            >
              <Box>
                {evaluatorDate && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextV2 variant="body1" color="textSecondary">
                      {t('ui.event-management.learner.profiles.from-evaluator')}
                      :
                    </TextV2>
                    <TextV2 variant="bodyBold" color="textSecondary">
                      {evaluatorDate}
                    </TextV2>
                  </Box>
                )}

                {purchaseDate && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextV2 variant="body1" color="textSecondary">
                      {t(
                        'ui.event-management.events.assign-chapters.purchased',
                      )}
                      :
                    </TextV2>
                    <TextV2 variant="bodyBold" color="textSecondary">
                      {purchaseDate}
                    </TextV2>
                  </Box>
                )}
              </Box>

              <Divider
                sx={(theme) => ({
                  mx: `-${theme.spacing(2)}`,
                  my: 1.5,
                })}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mt: 1,
                }}
              >
                <PreferredDialectAndPronoun
                  pronoun={getPronounIfSupportedByProfile(profile)}
                  preferredProfileDialect={profile.dialect}
                />
              </Box>
            </ProfileCardHeader>
          </Box>

          <Box
            sx={{
              mt: (theme) => theme.spacing(spacingSizeMap.L),
            }}
          >
            <ProfileCardActions
              onShare={onEmailProfile}
              onDownload={() => {}}
              disabled={disabled}
            />
          </Box>
        </CardContent>
      </Card>

      <DownloadProgressDialog
        open={showDownloadProgress}
        dialect={dialectString}
        onClose={() => setShowDownloadProgress(false)}
      />

      <EmailProgressDialog
        singleProfile
        open={showEmailProgress}
        dialect={dialectString}
        onClose={() => setShowEmailProgress(false)}
      />

      <DistributionMethodDialog
        open={showDistOptions}
        profileCount={1}
        onSendViaLink={(format) =>
          format === 'share-digital-profile-link'
            ? handleSendEmail(format)
            : setShowDistOptions(false)
        }
        onSendViaEmail={() => setShowDistOptions(false)}
        onCancel={() => setShowDistOptions(false)}
        showDigitalProfileLink
      />

      {showArchiveDialog && (
        <ProfileArchiveDialog
          open={showArchiveDialog}
          profileTitle={profileTitle}
          onCancel={handleProfileArchiveClose}
          archiveReason={archiveReason}
          setArchiveReason={setArchiveReason}
          archiveReasonOther={archiveReasonOther}
          setArchiveReasonOther={setArchiveReasonOther}
          onSubmit={handleArchiveProfile}
          archiveError={archiveError}
        />
      )}
    </>
  );
};

export default PersonalImpactProfileCard;
